/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import { useAppSelector } from '../../app/hooks';
// In webclient we created a little dummy commentsSlice.ts file to make the import work
// it's ugly - don't do this :-D  We could replace with a try { .. require() } catch() {} block
// but that's also ugly.
// eslint-disable-next-line import/no-restricted-paths
import { CommentType } from '../Comments/commentsSlice';
import { selectCreatorMode } from './chat/chatSlice';
import { DocumentVersionState, DocumentVersionType, PageTypeEnum, RoleType } from './algorithm/types';
import { selectFromSmartOnFhir, selectPatientContext } from './Fhir/fhirSlice';
import { isOrgCreator, PearlUser, selectPearlUserInfo, selectUserFlags, UserFlagsType } from './Login/userSlice';
import { getDocumentVersionPublicationBlockReason } from './documentVersionFlags/documentVersionFlags';
import { isPrePublished, isPreReview } from './utils';

export const isProduction = process.env.REACT_APP_ENVIRONMENT === 'production';

/** Get the user flags without needing a selector.  Bit ugly, but it means the permissions
 *  don't need to go through a selector.
 */
export function getUserFlags(): UserFlagsType | undefined {
  return window.store.getState().user.pearlUserInfo?.userFlags;
}

function flagEnabled(flag: keyof UserFlagsType): boolean {
  return !!getUserFlags()?.[flag];
}

/**
 * All of these permissions come from:
 *
 * https://pearl-project.atlassian.net/wiki/spaces/PearlArchitecture/pages/15433784/Document+Roles+and+Permissions
 */
function isReviewer(
  pearlUserInfo: PearlUser | undefined,
  documentVersion?: { document: DocumentVersionType['document'] }
) {
  return getRole(pearlUserInfo, documentVersion) === 'REVIEWER';
}

function isContributor(pearlUserInfo: PearlUser | undefined, documentVersion?: DocumentVersionType) {
  return getRole(pearlUserInfo, documentVersion) === 'CONTRIBUTOR';
}

function isOwner(
  pearlUserInfo: PearlUser | undefined,
  documentVersion?: { document: DocumentVersionType['document'] }
) {
  return pearlUserInfo ? documentVersion?.document.owner.id === pearlUserInfo.userId : false;
}

function isFacilitator(pearlUserInfo: PearlUser | undefined, documentVersion?: DocumentVersionType) {
  return getRole(pearlUserInfo, documentVersion) === 'FACILITATOR';
}

export function isOrgAdmin(pearlUserInfo: PearlUser | undefined) {
  return pearlUserInfo?.orgRole === 'ORG_ADMIN';
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function isCreator(pearlUserInfo: PearlUser | undefined) {
  return pearlUserInfo?.orgRole === 'CREATOR';
}

function isConsumer(pearlUserInfo: PearlUser | undefined) {
  return pearlUserInfo?.orgRole === 'CONSUMER';
}

export function canChangeHotPotato(pearlUserInfo: PearlUser | undefined, assigneeId?: string | null): boolean {
  // A user can always unassign themselves, meaning they are done.
  // A user can always assign someone else if nobody else is assigned
  // The only thing most users can't do is unassign someone else.

  if (!assigneeId || assigneeId === pearlUserInfo?.userId) return true;
  return true;
}

/**
 * For potential clients who would like to have a “trial” access to MedPearl will be given the MED_PEARL_ONE role.
 * Demo and Pilot end users will have this role (same permissions as consumer)
 */
export function isMedPearlOne(pearlUserInfo: PearlUser | undefined) {
  return pearlUserInfo?.orgRole === 'MED_PEARL_ONE';
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function isPublisher(pearlUserInfo: PearlUser | undefined) {
  return pearlUserInfo?.orgRole === 'PUBLISHER';
}
function getRole(
  pearlUserInfo: PearlUser | undefined,
  documentVersion?: { document: DocumentVersionType['document'] }
) {
  return isOwner(pearlUserInfo, documentVersion)
    ? 'OWNER'
    : documentVersion?.document.members?.find((member) => member.id === pearlUserInfo?.userId)?.role;
}

export function canCreateNewDocument(pearlUserInfo: PearlUser | undefined): boolean {
  return !isConsumer(pearlUserInfo) && !isMedPearlOne(pearlUserInfo);
}

export function canCreateNewDocumentVersion(
  pearlUserInfo: PearlUser | undefined,
  documentVersion?: DocumentVersionType
): boolean {
  if (!documentVersion) return false;
  if (
    documentVersion.state !== DocumentVersionState.PUBLISHED &&
    documentVersion.state !== DocumentVersionState.ARCHIVED
  )
    return false;
  return !isConsumer(pearlUserInfo) && !isMedPearlOne(pearlUserInfo);
}

export function canSubmitDocumentVersionForPublication(
  pearlUserInfo: PearlUser | undefined,
  documentVersion?: DocumentVersionType
): boolean {
  if (!isPreReview(documentVersion?.state)) return false;
  if (getDocumentVersionPublicationBlockReason(documentVersion)) return false;
  return isOrgAdmin(pearlUserInfo) || isOwner(pearlUserInfo, documentVersion);
}

export function canChangePrePublishStatus(
  pearlUserInfo: PearlUser | undefined,
  documentVersion?: DocumentVersionType
): boolean {
  if (!isPrePublished(documentVersion?.state)) return false;
  return true;
}

export function canRejectDocumentVersion(
  pearlUserInfo: PearlUser | undefined,
  documentVersion?: DocumentVersionType
): boolean {
  if (documentVersion?.state !== DocumentVersionState.REVIEW) return false;
  return isOrgAdmin(pearlUserInfo) || isPublisher(pearlUserInfo);
}

export function canPublishDocumentVersion(
  pearlUserInfo: PearlUser | undefined,
  documentVersion?: DocumentVersionType
): boolean {
  if (documentVersion?.state !== DocumentVersionState.REVIEW) return false;
  if (!flagEnabled('can_publish')) return false;
  if (getDocumentVersionPublicationBlockReason(documentVersion)) return false;
  return isOrgAdmin(pearlUserInfo) || isPublisher(pearlUserInfo);
}

export function canArchiveDocumentVersion(
  pearlUserInfo: PearlUser | undefined,
  documentVersion?: DocumentVersionType
): boolean {
  if (documentVersion?.state !== DocumentVersionState.PUBLISHED) return false;
  if (!flagEnabled('can_archive')) return false;
  return isOrgAdmin(pearlUserInfo) || isPublisher(pearlUserInfo);
}

export function canUnarchiveDocumentVersion(
  pearlUserInfo: PearlUser | undefined,
  documentVersion?: DocumentVersionType
): boolean {
  if (documentVersion?.state !== DocumentVersionState.ARCHIVED) return false;
  if (!flagEnabled('can_publish')) return false;
  if (getDocumentVersionPublicationBlockReason(documentVersion)) return false;
  return isOrgAdmin(pearlUserInfo) || isPublisher(pearlUserInfo);
}

export function getPublishActionDisabledReason(documentVersion?: DocumentVersionType) {
  return getDocumentVersionPublicationBlockReason(documentVersion);
}

export function isCreatorMode(pearlUserInfo: PearlUser | undefined): boolean {
  return isOrgCreator(pearlUserInfo?.orgRole);
}

export function canEditOrContextualizeDocumentVersion(
  pearlUserInfo: PearlUser | undefined,
  documentVersion?: DocumentVersionType
): boolean {
  if (!documentVersion) return false;
  if (isPrePublished(documentVersion?.state)) {
    return (
      isOrgAdmin(pearlUserInfo) ||
      isOwner(pearlUserInfo, documentVersion) ||
      isContributor(pearlUserInfo, documentVersion) ||
      isPublisher(pearlUserInfo)
    );
  } else if (documentVersion?.state === DocumentVersionState.REVIEW) {
    return isOrgAdmin(pearlUserInfo) || isPublisher(pearlUserInfo);
  } else if (documentVersion?.state === DocumentVersionState.PUBLISHED) {
    const edit_published_docs_flag = pearlUserInfo?.userFlags?.edit_published_docs;
    const can_write_library = pearlUserInfo?.writableLibraries?.some((lib) => lib.id === documentVersion?.library?.id);
    // There is a special launchdarkly flag edit_published_docs_flag that allows
    // a FEW users to edit published documents.  The backend respects this too and:
    //   - regardless of your role within the orgF
    //   - BUT your user must have writable access to library to which document belongs to!
    return !!edit_published_docs_flag && !!can_write_library;
  }
  return false;
}

export function canEditDocumentVersion(
  pearlUserInfo: PearlUser | undefined,
  documentVersion?: DocumentVersionType
): boolean {
  return canEditOrContextualizeDocumentVersion(pearlUserInfo, documentVersion);
}

export function canContextualizeDocumentVersion(
  pearlUserInfo: PearlUser | undefined,
  documentVersion?: DocumentVersionType
): boolean {
  return canEditOrContextualizeDocumentVersion(pearlUserInfo, documentVersion);
}

export function canDeleteDocumentVersion(
  pearlUserInfo: PearlUser | undefined,
  documentVersion?: DocumentVersionType
): boolean {
  if (isPrePublished(documentVersion?.state)) {
    return isOrgAdmin(pearlUserInfo) || isOwner(pearlUserInfo, documentVersion) || isPublisher(pearlUserInfo);
  }
  if (documentVersion?.state === DocumentVersionState.REVIEW) {
    return isOrgAdmin(pearlUserInfo) || isOwner(pearlUserInfo, documentVersion) || isPublisher(pearlUserInfo);
  }
  if (documentVersion?.state === DocumentVersionState.ARCHIVED) {
    if (!flagEnabled('can_archive')) return false;
    return isOrgAdmin(pearlUserInfo);
  }
  if (documentVersion?.state === DocumentVersionState.SNAPSHOT) {
    return isOrgAdmin(pearlUserInfo) || isOwner(pearlUserInfo, documentVersion) || isPublisher(pearlUserInfo);
  }
  return false;
}

export function canUnlockDocumentVersion(
  pearlUserInfo: PearlUser | undefined,
  documentVersion?: DocumentVersionType
): boolean {
  return isOrgAdmin(pearlUserInfo) || isOwner(pearlUserInfo, documentVersion) || isPublisher(pearlUserInfo);
}

export function canViewRoles(documentVersion: DocumentVersionType | undefined) {
  return isPrePublished(documentVersion?.state) || documentVersion?.state === DocumentVersionState.REVIEW;
}

export function canConvertDocType(pearlUserInfo: PearlUser | undefined, documentVersion?: DocumentVersionType) {
  return (
    isOrgAdmin(pearlUserInfo) &&
    (!documentVersion?.pages ||
      documentVersion.document.docType === 'TRIAGE' ||
      (documentVersion.document.docType === 'DOCUMENT' &&
        Object.values(documentVersion?.pages).every((page) => page.pageType !== PageTypeEnum.GUIDE)))
  );
}

// const environment = process.env.REACT_APP_ENVIRONMENT.toLowerCase();

export function canViewAuditLog(
  pearlUserInfo: PearlUser | undefined,
  documentVersion: DocumentVersionType | undefined
) {
  if (isOrgAdmin(pearlUserInfo) || isCreator(pearlUserInfo) || isPublisher(pearlUserInfo)) return true;
  return false;
}

export function canChangeRole(
  pearlUserInfo: PearlUser | undefined,
  documentVersion: DocumentVersionType | undefined,
  role: RoleType | undefined
): boolean {
  if (!documentVersion || !pearlUserInfo || !canViewRoles(documentVersion)) return false;
  const userRole = getRole(pearlUserInfo, documentVersion);

  if (isPreReview(documentVersion?.state)) {
    // -----
    // DRAFT etc
    // -----
    if (isOrgAdmin(pearlUserInfo)) return true;
    // 'role' is the NEW role that we're adding/removing
    switch (role) {
      case 'OWNER': // Change Document Owner
        return userRole === 'OWNER'; // only the ORG_ADMIN or owner can change owner
      case 'FACILITATOR': // Invite Document Facilitator
        return userRole === 'OWNER';
      case 'CONTRIBUTOR':
        return userRole === 'OWNER' || userRole === 'FACILITATOR';
      case 'REVIEWER':
        return userRole === 'OWNER' || userRole === 'FACILITATOR' || userRole === 'REVIEWER';
      case undefined:
        // Whether to show the drop down box at all.  Should equal all of the above, OR'd
        return userRole === 'OWNER' || userRole === 'FACILITATOR' || userRole === 'REVIEWER';
      default:
        return false;
    }
  }
  if (documentVersion?.state === DocumentVersionState.REVIEW) {
    // ------
    // REVIEW
    // ------
    switch (role) {
      case 'OWNER': // Change Document Owner
        return isOrgAdmin(pearlUserInfo) || isPublisher(pearlUserInfo);
      case 'FACILITATOR': // Invite Document Facilitator
        return false; // Cannot!
      case 'CONTRIBUTOR':
        return isOrgAdmin(pearlUserInfo) || isPublisher(pearlUserInfo);
      case 'REVIEWER':
        return isOrgAdmin(pearlUserInfo) || isPublisher(pearlUserInfo);
      case undefined:
        // Whether to show the drop down box at all.  Should equal all of the above, OR'd
        return isOrgAdmin(pearlUserInfo) || isPublisher(pearlUserInfo);
      default:
        return true;
    }
  }
  return false;
}

/* Commenting */
export function canSeeComments() {
  return true;
}

export function canAddComments(pearlUserInfo: PearlUser | undefined, documentVersion: DocumentVersionType | undefined) {
  if (!documentVersion) return false;
  if (isPrePublished(documentVersion.state)) {
    return (
      isOrgAdmin(pearlUserInfo) ||
      isOwner(pearlUserInfo, documentVersion) ||
      isFacilitator(pearlUserInfo, documentVersion) ||
      isContributor(pearlUserInfo, documentVersion) ||
      isReviewer(pearlUserInfo, documentVersion) ||
      isPublisher(pearlUserInfo)
    );
  }
  if (documentVersion.state === DocumentVersionState.REVIEW) {
    return (
      isOrgAdmin(pearlUserInfo) ||
      isOwner(pearlUserInfo, documentVersion) ||
      isReviewer(pearlUserInfo, documentVersion) ||
      isPublisher(pearlUserInfo)
    );
  }
  if (documentVersion.state === DocumentVersionState.PUBLISHED) {
    const edit_published_docs_flag = pearlUserInfo?.userFlags?.edit_published_docs;
    const can_write_library = pearlUserInfo?.writableLibraries?.some((lib) => lib.id === documentVersion?.library?.id);
    // There is a special launchdarkly flag edit_published_docs_flag that allows
    // a FEW users to edit published documents.  The backend respects this too and:
    //   - regardless of your role within the org
    //   - BUT your user must have writable access to library to which document belongs to!
    if (!(!!edit_published_docs_flag && !!can_write_library)) {
      return false;
    }
    return (
      isOrgAdmin(pearlUserInfo) ||
      isOwner(pearlUserInfo, documentVersion) ||
      isReviewer(pearlUserInfo, documentVersion) ||
      isPublisher(pearlUserInfo)
    );
  }
  return false;
}

/* Can archive comment or reopen comment */
export function canChangeCommentState(
  pearlUserInfo: PearlUser | undefined,
  documentVersion: { state: DocumentVersionState; document: DocumentVersionType['document'] } | undefined
) {
  if (!documentVersion) return false;
  if (isPrePublished(documentVersion.state)) {
    return (
      isOrgAdmin(pearlUserInfo) ||
      isOwner(pearlUserInfo, documentVersion) ||
      isPublisher(pearlUserInfo) ||
      isReviewer(pearlUserInfo, documentVersion)
    );
  }
  if (documentVersion.state === DocumentVersionState.REVIEW) {
    return isOrgAdmin(pearlUserInfo) || isPublisher(pearlUserInfo) || isOwner(pearlUserInfo, documentVersion);
  }
  if (documentVersion.state === DocumentVersionState.PUBLISHED) {
    const edit_published_docs_flag = pearlUserInfo?.userFlags?.edit_published_docs;
    const can_write_library = pearlUserInfo?.writableLibraries?.some(
      (lib) => lib.id === documentVersion?.document?.library?.id
    );
    // There is a special launchdarkly flag edit_published_docs_flag that allows
    // a FEW users to edit published documents.  The backend respects this too and:
    //   - regardless of your role within the org
    //   - BUT your user must have writable access to library to which document belongs to!
    if (!(!!edit_published_docs_flag && !!can_write_library)) {
      return false;
    }
    return (
      isOrgAdmin(pearlUserInfo) ||
      isOwner(pearlUserInfo, documentVersion) ||
      isReviewer(pearlUserInfo, documentVersion) ||
      isPublisher(pearlUserInfo)
    );
  }
  return false;
}

export function canDeleteComment(
  pearlUserInfo: PearlUser | undefined,
  documentVersion: { state: DocumentVersionState; document: DocumentVersionType['document'] } | undefined,
  comment: CommentType | undefined
) {
  if (!documentVersion) return false;
  if (isPrePublished(documentVersion.state)) {
    return (
      isOrgAdmin(pearlUserInfo) ||
      isOwner(pearlUserInfo, documentVersion) ||
      isReviewer(pearlUserInfo, documentVersion) ||
      isPublisher(pearlUserInfo) ||
      comment?.user.id === pearlUserInfo?.userId
    );
  }
  if (documentVersion.state === DocumentVersionState.REVIEW) {
    return isOrgAdmin(pearlUserInfo) || isPublisher(pearlUserInfo) || comment?.user.id === pearlUserInfo?.userId;
  }
  if (documentVersion.state === DocumentVersionState.PUBLISHED) {
    const edit_published_docs_flag = pearlUserInfo?.userFlags?.edit_published_docs;
    const can_write_library = pearlUserInfo?.writableLibraries?.some(
      (lib) => lib.id === documentVersion?.document?.library?.id
    );
    // There is a special launchdarkly flag edit_published_docs_flag that allows
    // a FEW users to edit published documents.  The backend respects this too and:
    //   - regardless of your role within the org
    //   - BUT your user must have writable access to library to which document belongs to!
    if (!(!!edit_published_docs_flag && !!can_write_library)) {
      return false;
    }
    return (
      isOrgAdmin(pearlUserInfo) ||
      isOwner(pearlUserInfo, documentVersion) ||
      isReviewer(pearlUserInfo, documentVersion) ||
      isPublisher(pearlUserInfo)
    );
  }
  return false;
}

/* Voting */
export function canSeeVotes() {
  return true; // I think?
}

export function canVote(pearlUserInfo: PearlUser | undefined, documentVersion: DocumentVersionType | undefined) {
  if (!isPrePublished(documentVersion?.state)) return false;
  return (
    isOrgAdmin(pearlUserInfo) ||
    isOwner(pearlUserInfo, documentVersion) ||
    isContributor(pearlUserInfo, documentVersion) ||
    isReviewer(pearlUserInfo, documentVersion)
  );
}

export function canResetVote(pearlUserInfo: PearlUser | undefined, documentVersion: DocumentVersionType | undefined) {
  if (!isPrePublished(documentVersion?.state)) return false;
  return isOrgAdmin(pearlUserInfo) || isOwner(pearlUserInfo, documentVersion);
}

export function canRemoveContextualization(
  pearlUserInfo: PearlUser | undefined,
  documentVersion: DocumentVersionType | undefined
) {
  if (!isPrePublished(documentVersion?.state)) return false;
  return isOrgAdmin(pearlUserInfo) && flagEnabled('can_remove_contextualization');
}

export function showAnalyticsDebug() {
  return flagEnabled('show_analytics_counts');
}

export function showPatientShowSummary() {
  return flagEnabled('patient_notes_summary');
}

export function enableContextualization() {
  return flagEnabled('show_contextualization');
}

export function enableDynamedTab() {
  return flagEnabled('dynamed_tab');
}

/** Whether this user has permission to edit fhir information.
 *  Note both the permissions and the user flag has to say it's allowed.
 */
export const useEditFhirUserFlag = () => {
  const flags = useAppSelector(selectUserFlags);
  const isCreatorMode = useAppSelector(selectCreatorMode);
  return isCreatorMode && !!flags?.edit_contextualization;
};

export const useDynamedTabUserFlag = () => {
  const flags = useAppSelector(selectUserFlags);
  return !!flags?.dynamed_tab;
};

/** For DEMO users or users with role "MedPearlOne" we show the medpearlOne feedback form.
 *
 *
 *  for others we show the default feedback form */
export const useNewFeedbackUserFlag = () => {
  const user = useAppSelector((state) => state.user.pearlUserInfo);
  const isCreatorMode = useAppSelector(selectCreatorMode);
  return !isCreatorMode && user?.orgCode && user?.orgCode?.toUpperCase() !== 'DEMO';
};

/** Whether this user has permission to view fhir information.
 *
 *  Note both the permissions and the user flag has to say it's allowed.
 */
export const useViewFhirUserFlag = () => {
  const patientContext = useAppSelector(selectPatientContext);
  const canUseCustomMRN = useCustomMRNUserFlag();
  const user = useAppSelector((state) => state.user.pearlUserInfo);
  const enabled = enableContextualization();
  if (!enabled) return false;
  const isTeamsAppUser = isTeamsUser(user);
  return !!(patientContext?.mrn || patientContext?.fhirPatientId || canUseCustomMRN) && !isTeamsAppUser;
};

/**
 * To validate the user is Teams user or not
 */
export function isTeamsUser(pearlUserInfo: PearlUser | undefined) {
  return pearlUserInfo?.sourceApp?.toUpperCase() === 'TEAMS';
}
export const useTellUsAboutYourExperienceBannerUserFlag = () => {
  const flags = useAppSelector(selectUserFlags);
  return !!flags?.show_banner;
};

export const useCustomMRNUserFlag = () => {
  const flags = useAppSelector(selectUserFlags);
  const isCreatorMode = useAppSelector(selectCreatorMode);
  return !!(isCreatorMode ? flags?.custom_MRN_in_creator : flags?.custom_MRN_in_consumer);
};

export const useCanEditPredefinedWidgetsUserFlag = () => {
  const flags = useAppSelector(selectUserFlags);
  return !!flags?.edit_predefined_widgets;
};

export const useAiConvertDocs = () => {
  const flags = useAppSelector(selectUserFlags);
  return !!flags?.['document-ai-chat-feature'];
};

export const showTrusana = () => {
  return flagEnabled('trusana');
};

export const showAlbertsons = () => {
  return flagEnabled('albertsons');
};

export const shouldFetchCareEverywhere = () => {
  return flagEnabled('CareEverywhere');
};

export const showPatientMedicines = () => {
  return flagEnabled('show-patient-medication');
};

export const showPatientProblemListAndPastMedicalHx = () => {
  return flagEnabled('show-patient-problem-list-past-medical-history');
};

let show_next_button = true;
export const setDebugOverrideNextButton = (value: boolean) => {
  show_next_button = value;
};

export const enableNextButton = () => {
  // I just cannot get flags to work here
  // return flagEnabled('next_button');
  return show_next_button;
};

export const enableMuscCPTCodes = () => {
  return flagEnabled('musc-contextualization');
};

export const enableAdminDownloadPatients = () => {
  return flagEnabled('admin-download-patient');
};

export const enableOpenAiForContextualization = () => {
  return flagEnabled('use-open-ai-for-contextualization');
};

export const isEnabledSuperAdminUI = () => flagEnabled('super-admin-ui');

export const isEnabledAffiliateAdminForUser = () => {
  return flagEnabled('manage-enrollments') || isEnabledSuperAdminUI();
};

export const isMuscUser = (pearlUserInfo?: PearlUser) => {
  return pearlUserInfo?.orgCode === 'MUSC';
};

export const useEnableEpicEmbeddedView = () => {
  const user = useAppSelector(selectPearlUserInfo);
  return useAppSelector(selectFromSmartOnFhir) && user?.userFlags?.['enable_epic_embedded_view'];
};

export const useIsConsumerWebUser = () => {
  const pearlUserInfo = useAppSelector(selectPearlUserInfo);
  const creatorMode = useAppSelector(selectCreatorMode);
  return !creatorMode && !isTeamsUser(pearlUserInfo);
};
